import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDateAndTime } from 'utils/date';

import { DeliverableNote } from 'types';

import './deliverable-approval-note.scss';

applyTranslations(require('./deliverable-approval-note.i18n.json'));

type Props = {
  notes?: Array<DeliverableNote>,
  noteValue: string,
  noteId?: number | null,
  uniqueId: number | string,
  type?: 'client' | 'influencer',
  isForBrand?: boolean,
  isZalandoOutfit?: boolean,
  isEntertainmentVideo?: boolean,
  showNotification?: boolean,
  isCampaignCreatorless?: boolean,
  onSend?: () => void,
  onCancel?: () => void,
  onNoteDelete?: (id: number) => void,
  onNoteEdit?: (id: number, text: string) => void,
  onNewNoteChange: (value: string) => void
}

const DeliverableApprovalNote = ({
  notes,
  noteValue,
  noteId,
  uniqueId,
  type = 'influencer',
  isCampaignCreatorless,
  isForBrand,
  isZalandoOutfit,
  isEntertainmentVideo,
  showNotification = true,
  onSend,
  onCancel,
  onNoteDelete,
  onNoteEdit,
  onNewNoteChange
}: Props) => {
  const divElement = React.useRef<HTMLDivElement>(null);

  const [collapsedContainerHeight, setCollapsedContainerHeight] = React.useState(0);
  const [isApprovalNotesCollapsed, setApprovalNotesCollapsed] = React.useState(true);

  React.useEffect(() => {
    if (divElement?.current) {
      setCollapsedContainerHeight(divElement.current.scrollHeight);
    }

    setApprovalNotesCollapsed(isEmpty(notes));
  }, [notes?.length, noteId]);

  const iconClasses = classnames('deliverable-approval-note__collapsible-icon', {
    'deliverable-approval-note__collapsible-icon--open': !isApprovalNotesCollapsed
  });

  const collapsibleContainerClasses = classnames('deliverable-approval-note__collapse-section', {
    'deliverable-approval-note__collapse-section--closed': isApprovalNotesCollapsed
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    onNewNoteChange(target.value || '');
  };

  const canEditDeliverableNote = (author: DeliverableNote['author']) => {
    if (!isCampaignCreatorless && author.type === 'brand') {
      return true;
    }

    return false;
  };

  const renderNoteDetails = (deliverableNote: DeliverableNote) => {
    const { date, author, isDeleted, isEdited } = deliverableNote;
    const nameFallback = __t(`deliverable_approval_note.name_fallback.${author.type}`);
    const editedSuffix = (isEdited && !isDeleted) ? __t('deliverable_approval_note.edited_marker') : '';
    const deletedSuffix = isDeleted ? __t('deliverable_approval_note.deleted_marker') : '';
    const noteAuthorName = author.type === 'moderator'
      ? __t('deliverable_approval_note.name_fallback.moderator')
      : author.name ?? nameFallback;
    const leftPart = `${deletedSuffix}${getDisplayDateAndTime(date)}`;
    const rightPart = `${noteAuthorName} ${editedSuffix}`;

    return (
      <div className="deliverable-approval-note__details">
        {`${leftPart} | ${rightPart}`}
      </div>
    );
  };

  const renderEditNote = (deliverableNote: DeliverableNote) => (
    <div
      key={deliverableNote.id}
      className="deliverable-approval-note__input"
    >
      {renderNoteDetails(deliverableNote)}
      <Input
        width="responsive"
        name="approval-note"
        placeholder={__t('deliverable_approval_note.placeholder')}
        value={noteValue}
        onChange={handleInputChange}
      />
    </div>
  );

  const renderActions = (text: string, id: number) => onNoteEdit && onNoteDelete && (
    <div className="deliverable-approval-note__item-menu">
      <SVGDynamicIcon
        name="pencil-custom-bg"
        customColor="grey"
        width={16}
        height={16}
        onClick={() => onNoteEdit(id, text)}
      />
      <SVGDynamicIcon
        name="trash-custom-fg"
        customColor="grey"
        width={16}
        height={16}
        onClick={() => onNoteDelete(id)}
      />
    </div>
  );

  const renderApprovalNote = (deliverableNote: DeliverableNote, index: number) => {
    const { text, author, id, isDeleted } = deliverableNote;
    const canEditNote = isForBrand && !isDeleted && canEditDeliverableNote(author);
    const wrapperClasses = classnames('deliverable-approval-note__item-wrapper', {
      'deliverable-approval-note__item-wrapper--edit-yes': canEditNote
    });

    return (
      <div
        key={`${uniqueId}-${index}`}
        className={wrapperClasses}
      >
        <div className="deliverable-approval-note__item">
          {renderNoteDetails(deliverableNote)}
          {
            !isDeleted && <div className="deliverable-approval-note__item-text">{text}</div>
          }
        </div>
        {canEditNote && !noteId && renderActions(text, id)}
      </div>
    );
  };

  const renderButtons = () => (
    <div className="deliverable-approval-note__actions">
      <ActionButton
        color="secondary"
        onClick={onSend}
        disabled={!(noteValue || '').trim()}
      >
        {__t('deliverable_approval_note.buttons.send')}
      </ActionButton>
      {
        noteId && onCancel && (
          <ActionButton
            color="tertiary"
            onClick={onCancel}
          >
            {__t('deliverable_approval_note.buttons.cancel')}
          </ActionButton>
        )
      }
    </div>
  );

  const noteDetailsClasses = classnames('deliverable-approval-note__section-title', {
    'deliverable-approval-note__section-title--notification': notes?.find(note => note.isNew) && showNotification
  });

  return (
    <div className="deliverable-approval-note__section">
      <div
        className="deliverable-approval-note__header"
        onClick={() => setApprovalNotesCollapsed(!isApprovalNotesCollapsed)}
      >
        <div className={noteDetailsClasses}>
          {__t(`deliverable_approval_note.header.${type}`)}
        </div>
        <div className={iconClasses}>
          <SVGDynamicIcon
            name="chevron-down-custom-fg"
            height={8}
            width={15}
            customColor="black"
            clickable
          />
        </div>
      </div>
      <div
        className={collapsibleContainerClasses}
        ref={divElement}
        style={{ maxHeight: isApprovalNotesCollapsed ? 0 : collapsedContainerHeight }}
      >
        {
          notes && (
            <div className="deliverable-approval-note__list">
              {notes?.map((note, index) => {
                if (noteId === note.id) {
                  return renderEditNote(note);
                }

                return renderApprovalNote(note, index);
              })}
            </div>
          )
        }
        {
          !noteId && (
            <div className="deliverable-approval-note__input">
              <Input
                width="responsive"
                name="approval-note"
                placeholder={__t('deliverable_approval_note.placeholder')}
                value={noteValue}
                onChange={handleInputChange}
              />
            </div>
          )
        }
        {(isZalandoOutfit || isEntertainmentVideo) && onSend && renderButtons()}
      </div>
    </div>
  );
};

export default DeliverableApprovalNote;
